<template>
  <nav class="navbar" :class="{ 'navbar-scrolled': isScrolled }">
    <div class="nav-content">
      <router-link to="/" class="nav-logo" @click="scrollToTop">
        <span class="logo-text">MethArDB</span>
      </router-link>
      <div class="nav-links">
        <router-link to="/" class="nav-link" :class="{ active: currentSection === 'home' }" @click="scrollToTop">Home</router-link>
        <div class="dropdown">
          <a href="#" 
             @click.prevent
             class="nav-link"
             :class="{ active: currentSection === 'database' }">
            Database
          </a>
          <div class="dropdown-content">
            <a href="#genome" @click.prevent="handleGenomeClick">Genome</a>
            <router-link to="/plasmid" @click="scrollToTop">Plasmid</router-link>
            <router-link to="/virus" @click="scrollToTop">Phage</router-link>
          </div>
        </div>
        <div class="dropdown" ref="analysisDropdown">
          <a href="#" 
             @click.prevent
             class="nav-link"
             :class="{ active: currentSection === 'analysis' }">
            Analysis Tools
          </a>
          <div class="dropdown-content" :class="{ 'dropdown-closed': dropdownClosed }">           
            <router-link to="/tools/ezaai" @click="closeDropdown">EzAAI</router-link>
            <router-link to="/checkm2" @click="closeDropdown">CHECKM2</router-link>
            <router-link to="/tools/ragtag" @click="closeDropdown">RagTag</router-link>
            <router-link to="/tools/ragout" @click="closeDropdown">Ragout</router-link>
            <router-link to="/tools/bakta" @click="closeDropdown">Bakta</router-link>
            <router-link to="/tools/tome" @click="closeDropdown">Tome OGT</router-link>
            <router-link to="/tools/diamond" @click="closeDropdown">MethArCT</router-link>
            <router-link to="/tools/metharct-beta" @click="closeDropdown">MethArCT-Beta</router-link>
          </div>
        </div>
        <router-link to="/about" class="nav-link" @click="scrollToTop">About</router-link>
      </div>
    </div>
  </nav>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'NavBar',
  setup() {
    const isScrolled = ref(false)
    const currentSection = ref('home')
    const analysisDropdown = ref(null)
    const dropdownClosed = ref(false)

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 50
    }

    const handlePageScroll = () => {
      const tableSection = document.querySelector('.table-section')
      if (tableSection) {
        const rect = tableSection.getBoundingClientRect()
        if (rect.top <= 120) {
          currentSection.value = 'database'
        } else {
          currentSection.value = 'home'
        }
      }
    }

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    }

    const closeDropdown = () => {
      dropdownClosed.value = true
      scrollToTop()
      // Reset after a short delay so hover works again next time
      setTimeout(() => {
        dropdownClosed.value = false
      }, 300)
    }

    const handleGenomeClick = () => {
      if (window.location.pathname === '/') {
        const element = document.querySelector('.table-section');
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } else {
        window.location.href = '/#genome';
      }
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll)
      window.addEventListener('scroll', handlePageScroll)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('scroll', handlePageScroll)
    })

    return {
      isScrolled,
      currentSection,
      analysisDropdown,
      dropdownClosed,
      handleGenomeClick,
      scrollToTop,
      closeDropdown
    }
  }
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  transition: all 0.3s;
}

.navbar-scrolled {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.nav-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-logo {
  text-decoration: none;
  color: #4169E1;
  font-size: 1.5rem;
  font-weight: bold;
}

.nav-links {
  display: flex;
  gap: 2rem;
}

.nav-link {
  text-decoration: none;
  color: #333;
  font-weight: 500;
  transition: color 0.3s;
  padding: 0.5rem 0;
}

.nav-link:hover,
.nav-link.active {
  color: #4169E1;
}

.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown > a {
  display: inline-block;
  padding: 0.5rem 0;
  text-align: left;
}

.dropdown-content {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  min-width: 100px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
}

.dropdown-content a {
  color: var(--text-primary);
  padding: 8px 12px;
  text-decoration: none;
  display: block;
  margin: 0 !important;
  font-weight: 500;
  font-size: 0.95rem;
  text-align: left;
  white-space: nowrap;
  transition: all 0.2s ease;
}

.dropdown:hover .dropdown-content {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-content.dropdown-closed {
  opacity: 0 !important;
  visibility: hidden !important;
  transform: translateY(-10px) !important;
}

.dropdown-content a:hover {
  background: #4169E1;
  color: white;
}

@media (max-width: 768px) {
  .nav-links {
    display: none;
  }
}
</style> 